<template>
  <div
    :class="[
      'task-container',
      { 'drag-and-drop-enabled': !isSmallScreenAndDown && isCurrentUserOwner },
      { 'view-only': viewOnly },
    ]"
  >
    <div :class="['task-title-container', { completed }]">
      <FriedIcon class="task-handle" icon="drag-handle" />
      <div :class="['counter', { 'past-deadline': isOverdue }]">
        <FriedIcon v-if="completed" :size="IconSize.Small" icon="bold-check-mark" />
        <span v-else>{{ taskOrder }}</span>
      </div>
      <div class="text-input-container">
        <FriedH4 class="map-title paragraph-large">{{ task.title }}</FriedH4>
        <InlineRichTextEditorViewer
          v-if="!emptyDescription"
          :content="formattedDescription"
          class="description"
          data-external="map-description-text"
        />
      </div>
    </div>
    <div v-if="!isXXSmallScreen" class="task-info-container">
      <div class="task-title-container task-info">
        <div class="task-status-container">
          <TaskCompletedBy
            v-if="completed && !!completedBy"
            :full-name="completedBy.fullName"
            :thumb-url="completedBy.thumbUrl"
          />
          <Deadline v-else-if="!completed && task.deadline" :deadline="task.deadline" />
        </div>
        <div class="task-participant-container">
          <FriedAvatarGroup
            v-if="task.participantIds.length > 0"
            class="task-owners"
            :avatars="getParticipantAvatars(selectableParticipants, task.participantIds)"
            :size="AvatarSize.Small"
            :visible-avatars="2"
          />
        </div>
      </div>
      <div
        v-if="!participantId && isCurrentUserOwner && !isSmallScreenAndDown"
        class="task-title-container"
      >
        <FriedDropdown
          :main-axis-offset="8"
          :cross-axis-offset="0"
          placement="bottom-end"
          :close-on-action="true"
        >
          <template #trigger>
            <FriedIconButton data-external="task-options-menu-button">
              <FriedIcon icon="ellipsis" />
            </FriedIconButton>
          </template>
          <template #content>
            <FriedDropdownItem
              v-for="option in taskOptions"
              :key="option.dataExternal"
              :data-external="option.dataExternal"
              @click="$emit(option.callback)"
            >
              <template #leftIcon>
                <FriedIcon :icon="option.icon" />
              </template>
              {{ option.title }}
            </FriedDropdownItem>
          </template>
        </FriedDropdown>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent } from 'vue';
import type { PropType } from 'vue';
import { AvatarSize, IconSize, ParagraphSize } from '@getaccept/fried-tofu';
import { fallbackParagraphContent, getDescriptionContent } from '@getaccept/editor-lib-new';
import type { User } from '@getaccept/lib-shared-new/src/users/types/user';
import { useI18n } from 'vue-i18n';
import useBreakpoints from '@getaccept/lib-shared-new/src/composables/breakpoints.composable';
import { DateTime } from 'luxon';
import { ParticipantRole } from '@getaccept/lib-shared-new/src/dealroom/types/enums/participant-role';
import InlineRichTextEditorViewer from '../../inline-text-editor/components/InlineRichTextEditorViewer.vue';
import { TaskHelper } from '../helpers/task.helper';
import { TaskStatus } from '../types/enums/task-status';
import type { Task } from '../types/action-plan';
import type { Participant } from '../../types';
import ActionPlanConstants from '../constants/action-plan.constants';
import Deadline from './Deadline.vue';
import TaskCompletedBy from './TaskCompletedBy.vue';

export default defineComponent({
  components: {
    Deadline,
    InlineRichTextEditorViewer,
    TaskCompletedBy,
  },
  props: {
    task: {
      type: Object as PropType<Task>,
      default: () => ({}),
    },
    taskOrder: {
      type: Number,
      default: 1,
    },
    participants: {
      type: Array as PropType<Participant[]>,
      default: () => [],
    },
    owner: {
      type: Object as PropType<User | Participant>,
      default: () => ({ id: '', contactId: '', role: ParticipantRole.Owner }),
    },
    participantId: {
      type: String,
      default: '',
    },
    isCurrentUserOwner: {
      type: Boolean,
    },
    viewOnly: {
      type: Boolean,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const { isXXSmallScreen, isSmallScreenAndDown } = useBreakpoints();

    const formattedDescription = computed(() => getDescriptionContent(props.task?.description));

    const emptyDescription = computed(
      () => !props.task?.description || props.task?.description === fallbackParagraphContent
    );

    const completed = computed(() => props.task?.status === TaskStatus.Completed);
    const isOverdue = computed(() => DateTime.now().toMillis() > props.task.deadline);

    const selectableParticipants = computed(() => [{ ...props.owner }, ...props.participants]);

    const completedBy = computed(() =>
      props.task?.status === TaskStatus.Completed
        ? selectableParticipants.value.find(
            participant => participant.id === props.task?.statusUpdatedBy
          )
        : undefined
    );

    return {
      isXXSmallScreen,
      isSmallScreenAndDown,
      emptyDescription,
      getParticipantAvatars: TaskHelper.getParticipantAvatars,
      taskOptions: ActionPlanConstants.taskOptions(),
      formattedDescription,
      completed,
      TaskStatus,
      ParagraphSize,
      AvatarSize,
      IconSize,
      t,
      selectableParticipants,
      completedBy,
      isOverdue,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/task';

$drag-handle-width: 1.25rem;

.description {
  margin: 0;
}

.task-container {
  min-height: 75px;
  cursor: pointer;
  padding: var(--spacing-100) var(--spacing-150);

  .task-handle {
    opacity: 0;
    cursor: grab;
    margin-left: calc($drag-handle-width * -1);
    transition: margin-left 0.2s ease-in-out;
  }

  .counter {
    transition: margin-left 0.2s ease-in-out;

    &.past-deadline {
      color: var(--error-dark);
      background-color: var(--error-light);
    }
  }

  &:hover:not(.view-only) {
    background-color: var(--blue-95);
  }

  &.view-only {
    padding: var(--spacing-100) 0;
    border-bottom: none;

    &:hover {
      cursor: default;
    }
  }

  &.drag-and-drop-enabled {
    &:hover {
      .task-handle {
        opacity: 1;
        margin-left: var(--spacing-25);
      }

      .counter {
        margin-left: var(--spacing-50);
      }
    }
  }
}
</style>
